import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { FeatureKey } from '@lynx-crypto/contracts/feature-flags';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { navItemPassesFeatureGate } from './navGating';

// Footer tab -> governing feature flag (parity with Sidebar). A section tab maps
// to the flag of its primary destination; the section's other routes are gated
// inside the hub itself (resolveSectionCards). Unmapped items are role-gated.
const NAV_FEATURE_BY_PATH: Record<string, FeatureKey> = {
  '/': 'dashboard',
  '/trading-hub': 'trading',
  '/strategies-hub': 'strategyBuilder',
  '/sentiment': 'sentiment',
  '/chart': 'chart',
  '/research': 'hunt',
  '/backtest': 'backtest',
  '/wallet-compare': 'walletCompare',
};

type NavItem = {
  path: string;
  label: string;
  icon: string;
  /** Routes (besides `path`) that should light this tab as active. */
  matchPrefixes?: string[];
  locked?: boolean | undefined;
  disabled?: boolean | undefined;
  disabledReason?: string | undefined;
};

// Spot-only destinations — the section tabs themselves stay enabled (the hub
// shows which cards are spot-only), but direct "More" links to these dim on a
// non-spot market, matching the Sidebar.
const TRADING_ITEMS_SPOT_ONLY = new Set<string>(['/backtest', '/wallet-compare']);

// ── Primary footer tabs: Dashboard · Trading · Strategies · Sentiment · More ──
// Trading and Strategies are SECTION tabs: they land on a hub and stay active
// across every route in their section.
const PRIMARY_NAV: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: 'grid' },
  {
    path: '/trading-hub',
    label: 'Trading',
    icon: 'trade',
    matchPrefixes: ['/trading', '/automated', '/journal', '/wallet-compare'],
  },
  {
    path: '/strategies-hub',
    label: 'Strategies',
    icon: 'target',
    matchPrefixes: ['/strategy', '/research', '/backtest'],
  },
  { path: '/sentiment', label: 'Sentiment', icon: 'heart' },
];

// "More" drawer: everything not promoted to a primary tab. Chart lives here
// (it's a deep-dive surface, not a section); System/Admin are admin-only.
const MORE_PUBLIC: NavItem[] = [
  { path: '/chart', label: 'Chart', icon: 'chart' },
];

const MORE_AUTH: NavItem[] = [
  { path: '/wallet', label: 'Holdings', icon: 'wallet' },
  { path: '/settings', label: 'Settings', icon: 'gear' },
];

const MORE_ADMIN: NavItem[] = [
  { path: '/system', label: 'System', icon: 'monitor' },
  { path: '/admin', label: 'Admin', icon: 'shield' },
];

function MobileIcon({ icon }: { icon: string }) {
  const cls = 'w-5 h-5';
  switch (icon) {
    case 'grid':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case 'trade':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="17 3 21 7 17 11" /><line x1="21" y1="7" x2="7" y2="7" />
          <polyline points="7 13 3 17 7 21" /><line x1="3" y1="17" x2="17" y2="17" />
        </svg>
      );
    case 'target':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
        </svg>
      );
    case 'heart':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case 'chart':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="9" y1="2" x2="9" y2="6" /><rect x="7" y="6" width="4" height="10" /><line x1="9" y1="16" x2="9" y2="22" />
          <line x1="17" y1="4" x2="17" y2="8" /><rect x="15" y="8" width="4" height="8" /><line x1="17" y1="16" x2="17" y2="20" />
        </svg>
      );
    case 'wallet':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 9h18" /><circle cx="16" cy="13" r="1.2" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'gear':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case 'monitor':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      );
    case 'shield':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case 'more':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="5" r="1.5" fill="currentColor" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
          <circle cx="12" cy="19" r="1.5" fill="currentColor" />
        </svg>
      );
    default:
      return null;
  }
}

/** True when `pathname` falls under this tab — its own path or any matchPrefix. */
function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.locked || item.disabled) return false;
  if (item.path === '/') return pathname === '/';
  const prefixes = [item.path, ...(item.matchPrefixes ?? [])];
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

function MobileNavDrawer({
  open,
  onClose,
  items,
}: {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
}) {
  const location = useLocation();
  const navigate = useNavigate();

  if (!open || items.length === 0) return null;

  const handleNav = (item: NavItem) => {
    if (item.disabled) return;
    navigate(item.locked ? '/login' : item.path);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} aria-hidden />
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-sidebar-bg border-t border-sidebar-border rounded-t-2xl safe-bottom">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <span className="text-sm font-semibold text-text-secondary">More</span>
          <button onClick={onClose} className="p-1 rounded-lg text-text-muted hover:text-text-primary" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1 px-2 pb-4">
          {items.map((item) => {
            const active = isItemActive(item, location.pathname);
            return (
              <button
                key={item.path}
                onClick={() => handleNav(item)}
                aria-disabled={item.disabled ? 'true' : undefined}
                title={item.disabledReason}
                className={`
                  flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl text-xs
                  transition-colors min-h-[64px] justify-center
                  ${item.disabled
                    ? 'text-text-muted/25 cursor-not-allowed'
                    : item.locked
                      ? 'text-text-muted/30'
                      : active
                        ? 'bg-accent/10 text-accent'
                        : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
                  }
                `}
              >
                <MobileIcon icon={item.icon} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const selectedMarket = useSettingsStore((s) => s.selectedMarket);
  const { canUse, loaded: flagsLoaded } = useFeatureAccess();

  // Drop nav items whose feature is disabled/out-of-tier; a `locked` item always
  // survives (auth-lock outranks the feature gate). Shared with Sidebar so both
  // surfaces gate identically — see navItemPassesFeatureGate.
  const flagAllows = (item: NavItem): boolean =>
    navItemPassesFeatureGate(item, NAV_FEATURE_BY_PATH, flagsLoaded, canUse);

  const lock = (item: NavItem): NavItem => ({ ...item, locked: true });
  const disableForMarket = (item: NavItem): NavItem => {
    if (selectedMarket === 'spot') return item;
    if (!TRADING_ITEMS_SPOT_ONLY.has(item.path)) return item;
    return {
      ...item,
      disabled: true,
      disabledReason: `${selectedMarket === 'margin' ? 'Margin' : 'Futures'} trading coming soon`,
    };
  };

  // Primary tabs. Section tabs (Trading/Strategies) padlock when signed out so a
  // visitor knows to sign in to use them; the hub itself shows finer card gating.
  const primaryItems = useMemo(
    () => {
      const prepared = isAuthenticated ? PRIMARY_NAV : PRIMARY_NAV.map((item) =>
        // Dashboard + Sentiment are public; Trading + Strategies need auth.
        item.path === '/' || item.path === '/sentiment' ? item : lock(item),
      );
      return prepared.filter(flagAllows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, selectedMarket, canUse, flagsLoaded],
  );

  const moreItems = useMemo(() => {
    const items: NavItem[] = [...MORE_PUBLIC];
    items.push(
      ...(isAuthenticated ? MORE_AUTH.map(disableForMarket) : MORE_AUTH.map(lock)),
    );
    // Admin entries are ADMIN-ONLY: hidden entirely for non-admins (parity with
    // Sidebar). Admin is an operator surface ordinary users can never reach, so
    // it is simply absent rather than a padlock.
    if (role === 'admin') items.push(...MORE_ADMIN);
    return items.filter(flagAllows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, role, selectedMarket, canUse, flagsLoaded]);

  const isMoreActive = moreItems.some((item) => isItemActive(item, location.pathname));
  const hasMore = moreItems.length > 0;

  return (
    <>
      <nav className="flex items-center justify-around bg-sidebar-bg border-t border-sidebar-border py-1 px-1 safe-bottom">
        {primaryItems.map((item) => {
          const active = isItemActive(item, location.pathname);
          return (
            <button
              key={item.path}
              onClick={() => {
                if (item.disabled) return;
                navigate(item.locked ? '/login' : item.path);
              }}
              aria-disabled={item.disabled ? 'true' : undefined}
              title={item.disabledReason}
              className={`
                flex flex-1 flex-col items-center gap-0.5 px-1 py-2 rounded-lg text-[11px]
                min-h-[44px] justify-center transition-colors
                ${item.disabled
                  ? 'text-text-muted/25 cursor-not-allowed'
                  : item.locked
                    ? 'text-text-muted/30'
                    : active
                      ? 'text-accent'
                      : 'text-text-muted'}
              `}
            >
              <MobileIcon icon={item.icon} />
              <span className="truncate max-w-full">{item.label}</span>
            </button>
          );
        })}
        {hasMore && (
          <button
            onClick={() => setDrawerOpen(true)}
            className={`
              flex flex-1 flex-col items-center gap-0.5 px-1 py-2 rounded-lg text-[11px]
              min-h-[44px] justify-center transition-colors
              ${isMoreActive ? 'text-accent' : 'text-text-muted'}
            `}
          >
            <MobileIcon icon="more" />
            <span>More</span>
          </button>
        )}
      </nav>
      <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} items={moreItems} />
    </>
  );
}
